import { useCallback, useEffect, useState } from "react";
import { Bookmark, BookmarkPlus, MoreHorizontal, Users, Trash2, Share2, User, Ban, Terminal } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

export const EMPTY_FILTERS = { q: "", assignee: "", type: "", priority: "", label: "", blocked: false, query: "", mode: "simple" };
const COMPARE_KEYS = Object.keys(EMPTY_FILTERS).filter((k) => k !== "mode");
const PRESETS = [
  { id: "mine", nameKey: "presetMine", icon: User, filters: { assignee: "me" } },
  { id: "blocked", nameKey: "presetBlocked", icon: Ban, filters: { blocked: true } },
];

export function hasFilters(f) {
  return !!(f.q || f.assignee || f.type || f.priority || f.label || f.blocked || (f.mode === "query" && f.query));
}

export function isQueryMode(f) {
  return f.mode === "query";
}

export function useProjectFilters(projectId) {
  const key = `apha_filters_${projectId}`;
  const [filters, setFilters] = useState(() => {
    try { return { ...EMPTY_FILTERS, ...(JSON.parse(localStorage.getItem(key) || "{}")) }; } catch { return EMPTY_FILTERS; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(filters)); }, [key, filters]);
  return [filters, setFilters];
}

function sameFilters(a, b) {
  return COMPARE_KEYS.every((k) => (a[k] || EMPTY_FILTERS[k]) === (b[k] || EMPTY_FILTERS[k]));
}

function withMode(f) {
  return { ...EMPTY_FILTERS, ...f, mode: f.query ? "query" : "simple" };
}

function Chip({ id, label, icon: Icon, active, onClick, menu, shared, title, dashed }) {
  return (
    <div className={`group inline-flex items-center rounded-full border text-xs transition-colors ${dashed ? "border-dashed" : ""} ${active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white hover:border-[#1E3A8A]/50 text-slate-700"}`} title={title}>
      <button type="button" data-testid={`quick-filter-${id}`} onClick={onClick} className={`inline-flex items-center gap-1.5 pl-2.5 py-1 ${menu ? "pr-1" : "pr-2.5"}`}>
        {Icon && <Icon className="h-3 w-3" />}
        {shared && <Users className="h-3 w-3 opacity-70" />}
        <span className="max-w-[10rem] truncate">{label}</span>
      </button>
      {menu}
    </div>
  );
}

function SaveViewPopover({ projectId, canShare, filters, swimlane, onSaved }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const disabled = !hasFilters(filters);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = isQueryMode(filters) ? { query: filters.query } : { ...filters, query: "", mode: undefined };
    try {
      await api.post(`/projects/${projectId}/saved-filters`, { name, filters: payload, swimlane: swimlane && swimlane !== "none" ? swimlane : null, shared });
      toast.success(t("viewSaved"));
      setOpen(false); setName(""); setShared(false); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="save-view-btn" disabled={disabled} title={disabled ? t("noFiltersToSave") : t("saveView")}
          className="inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-xs text-[#1E3A8A] hover:bg-[#DBEAFE]/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <BookmarkPlus className="h-3 w-3" />{t("saveView")}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72" data-testid="save-view-popover">
        <form onSubmit={save} className="space-y-3">
          <div className="text-sm font-semibold font-heading">{t("saveView")}</div>
          <Input autoFocus data-testid="save-view-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder={t("viewNamePh")} className="h-8 text-sm" />
          {canShare && (
            <label className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{t("shareWithProject")}</span>
              <Switch data-testid="save-view-shared" checked={shared} onCheckedChange={setShared} />
            </label>
          )}
          <Button type="submit" size="sm" className="w-full" disabled={busy || !name.trim()} data-testid="save-view-submit">{t("save")}</Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

function ChipMenu({ view, canShare, onChanged }) {
  const { t } = useLang();
  const patch = async (body, msg) => {
    try { await api.patch(`/saved-filters/${view.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/saved-filters/${view.id}`); toast.success(t("viewDeleted")); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`quick-filter-menu-${view.id}`} className="pr-1.5 pl-0.5 py-1 opacity-60 group-hover:opacity-100 transition-opacity" aria-label="View options">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="text-xs">
        {canShare && (
          <DropdownMenuItem data-testid={`quick-filter-share-${view.id}`} onClick={() => patch({ shared: !view.shared }, t(view.shared ? "viewUnshared" : "viewShared"))}>
            <Share2 className="h-3.5 w-3.5 mr-2" />{view.shared ? t("unshare") : t("share")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`quick-filter-delete-${view.id}`} onClick={remove} className="text-[#991B1B]">
          <Trash2 className="h-3.5 w-3.5 mr-2" />{t("deleteView")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function QuickFilters({ projectId, summary, filters, setFilters, swimlane, setSwimlane }) {
  const { t } = useLang();
  const [views, setViews] = useState([]);
  const load = useCallback(() => {
    api.get(`/projects/${projectId}/saved-filters`).then((r) => setViews(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const canShare = summary.access === "MANAGE";
  const queryMode = isQueryMode(filters);
  const apply = (f, lane) => {
    const next = withMode(f);
    if (sameFilters(filters, next)) { setFilters(EMPTY_FILTERS); return; }
    setFilters(next);
    if (lane && setSwimlane) setSwimlane(lane);
  };
  const toggleQuery = () => setFilters(queryMode ? EMPTY_FILTERS : { ...EMPTY_FILTERS, mode: "query" });
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-filters">
      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground mr-1"><Bookmark className="h-3 w-3" />{t("quickFilters")}</span>
      {PRESETS.map((p) => (
        <Chip key={p.id} id={`preset-${p.id}`} label={t(p.nameKey)} icon={p.icon} active={sameFilters(filters, withMode(p.filters))} onClick={() => apply(p.filters)} />
      ))}
      {views.map((v) => (
        <Chip key={v.id} id={`chip-${v.id}`} label={v.name} shared={v.shared} active={sameFilters(filters, withMode(v.filters))}
          icon={v.filters?.query ? Terminal : undefined}
          title={v.filters?.query ? v.filters.query : v.shared && !v.is_owner ? `${t("sharedBy")} ${v.owner_name || ""}` : undefined}
          onClick={() => apply(v.filters, v.swimlane)}
          menu={v.can_edit ? <ChipMenu view={v} canShare={canShare} onChanged={load} /> : null} />
      ))}
      <Chip id="query-toggle" label={t("queryMode")} icon={Terminal} active={queryMode} onClick={toggleQuery} dashed title={t("queryModeHint")} />
      <SaveViewPopover projectId={projectId} canShare={canShare} filters={filters} swimlane={swimlane} onSaved={load} />
    </div>
  );
}
